/**
 * In-app notifications — the vocabulary "tell somebody something happened" is written in.
 *
 * ## The set of types is fixed and owned by the code
 *
 * A notification type is not a row a workspace creates. It is a **trigger that exists in the
 * source**: something calls `notify()` with it, and nothing else ever produces one. Adding a type
 * is therefore a deploy — a new call site, a new sentence in both catalogues, a new row in the
 * preference matrix — never an INSERT. That single fact decides the whole shape below, and in
 * particular decides the preferences model (see `NotificationPreferences`).
 *
 * ## A notification stores facts, not a sentence
 *
 * `Notification` carries its `type` and a small bag of `data`; the sentence is rendered when
 * somebody looks at it, from the catalogue, in **the reader's current locale**. This is the same
 * decision `ApiError.messageKey` makes in `frontend/src/lib/api.ts` and for the same reason: a
 * notification outlives the request that created it, and a person who switches language must see
 * their feed re-read in the new one rather than frozen in the language the actor happened to be
 * using. Storing a rendered title and body would make the language of every past notification a
 * property of whoever triggered it.
 *
 * The email is the one exception, and not really an exception: it is rendered once, at send time,
 * in the **recipient's** stored locale, exactly like every other message this product sends
 * (`backend/src/lib/mail/render.ts`).
 *
 * ## `data` is read forgivingly
 *
 * Every value in the bag is a string, and a renderer asks for the keys it needs with a fallback.
 * A row written by a deploy that put one more key in it stays renderable by an older one, and a
 * row written by an older deploy stays renderable by a newer one. Same contract as
 * `readPermissions`: the writer is strict, the reader is forgiving.
 */

/* ── the types ────────────────────────────────────────────────────────────── */

/**
 * Every notification this product can produce, in the order the preferences matrix lists them.
 *
 * Each one names a real trigger that exists today. There are no placeholder types: a type nothing
 * calls is a row in the preferences screen that can never do anything, which teaches a person
 * that the switches are decorative.
 *
 *  - `POST_ASSIGNED` — a colleague handed you a post on a brand's workflow board.
 *    (`modules/workflow/service.ts#patchCard`)
 *  - `APPROVAL_REQUESTED` — somebody put a post in front of you for review.
 *    (`PUT /api/v2/scheduler/posts/:id/approvals` with a non-final state, which is how the
 *    Metricool-compatible surface says "please look at this")
 *  - `REVIEW_DECIDED` — an external reviewer, through a brand's review portal link, approved or
 *    rejected a post. (`modules/reviewportal/service.ts#decide`)
 *  - `FLOW_NOTIFIED` — one of the brand's own Flows told you about a post, because an event you
 *    or a colleague configured it to watch for happened. (`modules/flows/actions.ts`, the `NOTIFY`
 *    action; `shared/src/flows.ts` lists every event that can produce one)
 *  - `AUTOMATION_NOTIFIED` — same idea, for the brand's own Automazioni rules: a `NOTIFY_TEAM`
 *    action node fired. (`modules/automations/engine.ts`; `shared/src/automations.ts` lists every
 *    trigger that can lead to one) Separate from `FLOW_NOTIFIED` rather than reused, because the
 *    two engines are separate features with separate screens — a person switching this one off
 *    must not also go deaf to the other.
 *  - `CONNECTION_NEEDS_ATTENTION` — a brand's network connection has been expired or erroring for
 *    days and nobody has reconnected it. (`jobs/engagementNudges.ts`)
 *  - `BRAND_GONE_QUIET` — a brand with working connections has published nothing for weeks and has
 *    nothing scheduled. (`jobs/engagementNudges.ts`)
 *
 * ## The last two are not like the others
 *
 * Every type above them names something a **person did**: a colleague assigned, a reviewer decided,
 * a Flow somebody configured fired. The two nudges name something that **did not happen** — nobody
 * reconnected, nobody published — which is the only kind of fact a product has to go looking for,
 * because there is no event to hang it on. That difference is the whole reason `jobs/
 * engagementNudges.ts` exists and the whole reason it carries a cooldown ledger: an absence is
 * still true tomorrow, so a trigger that fires on one would fire on it every day for ever unless
 * something remembers that it already said so. See `EngagementNudge` in the schema.
 */
export const NOTIFICATION_TYPES = [
  'POST_ASSIGNED',
  'APPROVAL_REQUESTED',
  'REVIEW_DECIDED',
  'FLOW_NOTIFIED',
  'AUTOMATION_NOTIFIED',
  'CONNECTION_NEEDS_ATTENTION',
  'BRAND_GONE_QUIET',
] as const

export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

export const isNotificationType = (value: unknown): value is NotificationType =>
  typeof value === 'string' && (NOTIFICATION_TYPES as readonly string[]).includes(value)

/* ── preferences ──────────────────────────────────────────────────────────── */

/**
 * The two ways one type can reach a person.
 *
 * They are independent booleans rather than a level, for the reason `permissions.ts` gives about
 * its own keys: "email but not in-app" is a real choice (somebody who lives in their inbox and
 * never opens the bell), and so is "in-app but not email", and no ordering of the two produces
 * both. `inApp` is also the switch that decides whether a **row exists at all** — see `notify()`.
 */
export interface NotificationChannels {
  /** Write a row into the recipient's feed. Off means no row, not a hidden row. */
  inApp: boolean
  /** Also send the message by email, through the ordinary mail queue. */
  email: boolean
}

/** A complete answer: every type, both channels decided. Never partial. */
export type NotificationPreferences = Record<NotificationType, NotificationChannels>

/** What a partially-written preference row, or a partial `PUT` body, looks like. */
export type PartialNotificationPreferences = Partial<
  Record<NotificationType, Partial<NotificationChannels>>
>

/**
 * What a person gets before they have ever opened the preferences screen.
 *
 * **In-app is on for everything.** The feed is the product telling you about your own work; it
 * interrupts nobody, costs a row, and a bell that starts empty because every switch defaults off
 * would teach people the feature is broken.
 *
 * **Email is on for `POST_ASSIGNED` only.** It is the one type that *hands somebody work*: exactly
 * one recipient, chosen by name by a colleague, who would otherwise learn about it only by opening
 * a board they may not open today. The others are answers about work the recipient already owns and
 * will meet the next time they look at it, and two of them fan out to everybody who could have
 * reviewed the post — which is precisely the shape that turns into mail nobody reads, and then into
 * a filter, and then into a person who misses the one message that mattered. Defaulting them to
 * in-app only keeps the inbox meaning "somebody is waiting on you".
 *
 * `FLOW_NOTIFIED` is in-app only for a reason of its own, and a stronger one: it is the only type
 * produced by a **standing rule** rather than by a person doing something. A Flow on "every
 * published post" fires as often as the brand publishes, for ever, with nobody in the loop deciding
 * that this particular one was worth an interruption. Defaulting that to email would be the product
 * volunteering somebody's inbox for a recurring job. The switch is there for anybody who wants it.
 *
 * **The two nudges split, and the split is the point.**
 *
 * `CONNECTION_NEEDS_ATTENTION` defaults to **email on**, and it is the only type besides
 * `POST_ASSIGNED` that does. A broken connection is the product quietly not working: nothing is
 * being collected from that network, and anything scheduled to it will fail. It hands somebody a
 * job only they can do — re-granting access — and the person who most needs telling is precisely
 * the one who is *not* opening the app, because from inside the app nothing looks wrong. It is also
 * the rarest thing here: `jobs/engagementNudges.ts` sends at most one per brand per week, and only
 * while the connection is still broken.
 *
 * `BRAND_GONE_QUIET` defaults to **in-app only**, and would be a mistake as an email. "You have not
 * posted in a while" is a remark about the recipient's own behaviour that they did not ask for, from
 * software, in their inbox — which is the exact shape of the mail people build a filter for, and the
 * filter catches `CONNECTION_NEEDS_ATTENTION` on its way past. Keeping it in the bell means the one
 * nudge that is genuinely actionable keeps its way into the inbox. Anybody who wants it by mail has
 * a switch; nobody is volunteered.
 */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  POST_ASSIGNED: { inApp: true, email: true },
  APPROVAL_REQUESTED: { inApp: true, email: false },
  REVIEW_DECIDED: { inApp: true, email: false },
  FLOW_NOTIFIED: { inApp: true, email: false },
  AUTOMATION_NOTIFIED: { inApp: true, email: false },
  CONNECTION_NEEDS_ATTENTION: { inApp: true, email: true },
  BRAND_GONE_QUIET: { inApp: true, email: false },
}

/** A fresh, mutable copy of the defaults. The only way a preference set is built from nothing. */
export const defaultNotificationPreferences = (): NotificationPreferences =>
  Object.fromEntries(
    NOTIFICATION_TYPES.map((type) => [type, { ...DEFAULT_NOTIFICATION_PREFERENCES[type] }]),
  ) as NotificationPreferences

/**
 * Fill in what a partial set does not say, from the defaults.
 *
 * Note the asymmetry with `resolvePermissions`, which fills absent keys with `false`: an absent
 * permission is "not granted", but an absent *preference* is "never expressed an opinion", and the
 * honest answer to that is the default rather than off. A body that turns one switch off must not
 * silently turn the other fifteen off with it.
 */
export const resolveNotificationPreferences = (
  input: PartialNotificationPreferences,
): NotificationPreferences => {
  const out = defaultNotificationPreferences()
  for (const type of NOTIFICATION_TYPES) {
    const patch = input[type]
    if (patch === undefined) continue
    if (typeof patch.inApp === 'boolean') out[type].inApp = patch.inApp
    if (typeof patch.email === 'boolean') out[type].email = patch.email
  }
  return out
}

/**
 * Read a stored JSON column into a `NotificationPreferences`.
 *
 * Mirrors `readPermissions` and `readFeatures`: unknown types are dropped, absent ones take the
 * default, and anything that is not an object of objects degrades to the defaults rather than
 * throwing. A malformed row must still leave a person with a working bell and an editable screen.
 */
export const readNotificationPreferences = (value: unknown): NotificationPreferences => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return defaultNotificationPreferences()
  }
  const source = value as Record<string, unknown>
  const input: PartialNotificationPreferences = {}
  for (const type of NOTIFICATION_TYPES) {
    const raw = source[type]
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) continue
    const entry = raw as Record<string, unknown>
    const patch: Partial<NotificationChannels> = {}
    if (typeof entry['inApp'] === 'boolean') patch.inApp = entry['inApp']
    if (typeof entry['email'] === 'boolean') patch.email = entry['email']
    input[type] = patch
  }
  return resolveNotificationPreferences(input)
}

/* ── the feed, on the wire ────────────────────────────────────────────────── */

/**
 * The facts a notification's sentence is rendered from.
 *
 * Every value is a string so the bag survives JSON, the database column and a locale change
 * without a schema per type. The keys each type uses:
 *
 *  - `POST_ASSIGNED` — `actorName`, `brandName`, `excerpt`
 *  - `APPROVAL_REQUESTED` — `actorName`, `brandName`, `excerpt`
 *  - `REVIEW_DECIDED` — `reviewerName`, `brandName`, `excerpt`, `decision` (`APPROVED`/`REJECTED`)
 *  - `FLOW_NOTIFIED` — `flowName`, `brandName`, `excerpt`
 *  - `AUTOMATION_NOTIFIED` — `ruleName`, `brandName`, `excerpt`
 *  - `CONNECTION_NEEDS_ATTENTION` — `brandName`, `network` (one provider's name, the first one when
 *    several are broken), `count` (how many of the brand's connections need attention, as a
 *    decimal string), `days` (how long the oldest has been broken)
 *  - `BRAND_GONE_QUIET` — `brandName`, `days` (since the last published post, or since the brand
 *    was created when it has never published)
 */
export type NotificationData = Readonly<Record<string, string>>

/**
 * Where clicking a notification goes.
 *
 * Wire ids, because that is what the console navigates by: `blogId` picks the brand and
 * `plannerPostId` opens the post in the planner (`/app/planner?post=<id>`). Either may be null —
 * a notification about something that no longer has a post is still a true statement about the
 * past, and the row simply does not navigate.
 */
export interface NotificationLink {
  blogId: number | null
  brandName: string | null
  plannerPostId: number | null
}

/** One row of the feed. */
export interface NotificationView {
  id: string
  type: NotificationType
  data: NotificationData
  link: NotificationLink
  /** ISO-8601 UTC, or null while unread. */
  readAt: string | null
  /** ISO-8601 UTC. */
  createdAt: string
}

/** `GET /api/v1/notifications/unread-count`. */
export interface NotificationUnreadCount {
  /** Clamped — see `UNREAD_COUNT_CAP`. A badge does not need to be able to say 4,312. */
  unread: number
  /** True when the real number is above the cap and `unread` is therefore the cap. */
  capped: boolean
}

/**
 * The highest number the unread badge will ever report.
 *
 * Counting is cheap, but a badge reading "99+" and a badge reading "4312" say the same thing to a
 * person and only one of them fits. The cap is applied server-side so the number on the wire is
 * already the number to draw.
 */
export const UNREAD_COUNT_CAP = 99

/** How many rows one feed page may carry. */
export const NOTIFICATION_PAGE_SIZE = 20
export const MAX_NOTIFICATION_PAGE_SIZE = 50
